import { useState, useEffect, useCallback, useRef } from 'react'
import { LayoutGrid, LayoutList, Activity, AlertTriangle, CheckCircle, Clock, RefreshCw, XCircle, Archive, Download } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import VideoList from './VideoList'
import StreamTable from './StreamTable'
import VideoDetail from './VideoDetail'
import VideoUpload from './VideoUpload'
import BulkTagModal from '../../../components/BulkTagModal'
import ConfirmArchiveModal from '../../../components/ConfirmArchiveModal'
import '../../../components/BulkTagModal.css'
import './StreamManager.css'

export default function StreamManager() {
  const { api } = useAuth()
  const [videos, setVideos] = useState([])
  const [selected, setSelected] = useState(null)
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [paging, setPaging] = useState({
    page: 1,
    perPage: 25,
    search: ''
  })

  // Tag filter
  const [allTags, setAllTags] = useState([])
  const [activeTag, setActiveTag] = useState('')
  const [tagFilterUIDs, setTagFilterUIDs] = useState(null)

  // Bulk selection
  const [checked, setChecked] = useState(new Set())
  const [showBulkTag, setShowBulkTag] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [bulkBusy, setBulkBusy] = useState(false)

  // VOD link status per stream UID
  const [vodLinks, setVodLinks] = useState({})
  const [viewMode, setViewMode] = useState('table') // 'table' or 'cards'

  // Archive status per stream UID
  const [archiveStatuses, setArchiveStatuses] = useState({})
  const [archiveFilter, setArchiveFilter] = useState('') // '' | 'done' | 'error' | 'in_progress' | 'none'

  // Archive queue panel
  const [showQueue, setShowQueue] = useState(false)
  const [queueData, setQueueData] = useState(null)
  const [queueLoading, setQueueLoading] = useState(false)
  const archiveRefreshRef = useRef(null)

  // Download request tracking: { uid: { title, status, pctComplete, requestedAt } }
  const [downloadTracking, setDownloadTracking] = useState({})
  const downloadRefreshRef = useRef(null)

  useEffect(() => {
    api('/tags').then(d => setAllTags(d.tags || [])).catch(() => {})
  }, [api])

  useEffect(() => {
    if (!activeTag) {
      setTagFilterUIDs(null)
      return
    }
    api(`/tags/filter?tag=${encodeURIComponent(activeTag)}`)
      .then(d => setTagFilterUIDs(d.stream_uids || []))
      .catch(() => setTagFilterUIDs([]))
  }, [activeTag, api])

  const loadVideos = useCallback(async () => {
    setLoading(true)
    setStatus('Loading videos...')
    
    try {
      const params = new URLSearchParams({
        search: paging.search,
        page: paging.page,
        per_page: paging.perPage
      })
      
      const data = await api(`/videos?${params}`)
      
      let list = []
      if (Array.isArray(data)) {
        list = data
      } else if (Array.isArray(data.result)) {
        list = data.result
      } else if (data.result?.videos && Array.isArray(data.result.videos)) {
        list = data.result.videos
      } else if (data.videos && Array.isArray(data.videos)) {
        list = data.videos
      } else if (data.data && Array.isArray(data.data)) {
        list = data.data
      }
      
      setVideos(list)
      setStatus(`${list.length} videos loaded`)

      // Load VOD link status, archive statuses, and download statuses for all videos
      loadVodLinksForVideos(list)
      loadArchiveStatuses(list)
      loadDownloadStatuses(list)
    } catch (e) {
      console.error('Load videos error:', e)
      setStatus(`Error: ${e.message}`)
      setVideos([])
    } finally {
      setLoading(false)
    }
  }, [api, paging])

  // Batch-load VOD link status for visible videos
  async function loadVodLinksForVideos(videoList) {
    const links = {}
    // Fire all requests in parallel for speed
    const results = await Promise.allSettled(
      videoList.map(async (v) => {
        try {
          const res = await api(`/content/vod-link/${v.uid}`)
          return { uid: v.uid, data: res }
        } catch {
          return { uid: v.uid, data: { linked: false } }
        }
      })
    )
    for (const r of results) {
      if (r.status === 'fulfilled' && r.value) {
        links[r.value.uid] = r.value.data
      }
    }
    setVodLinks(links)
  }

  // Batch-load archive status for visible videos
  async function loadArchiveStatuses(videoList) {
    if (!videoList.length) return
    try {
      const uids = videoList.map(v => v.uid)
      const data = await api('/archive-status/batch', {
        method: 'POST',
        body: JSON.stringify({ uids })
      })
      if (data.results) setArchiveStatuses(data.results)
    } catch (e) {
      console.error('Failed to load archive statuses:', e)
    }
  }

  // Batch-load MP4 download status for visible videos
  async function loadDownloadStatuses(videoList) {
    if (!videoList.length) return
    try {
      const uids = videoList.map(v => v.uid).filter(Boolean)
      const data = await api('/download-status/batch', {
        method: 'POST',
        body: JSON.stringify({ uids })
      })
      if (data.results) {
        setDownloadTracking(prev => {
          const updated = { ...prev }
          for (const [uid, info] of Object.entries(data.results)) {
            updated[uid] = { status: info.status, pctComplete: info.pctComplete, url: info.url }
          }
          return updated
        })
      }
    } catch (e) {
      console.error('Failed to load download statuses:', e)
    }
  }

  // Load archive queue data
  async function loadQueue() {
    setQueueLoading(true)
    try {
      const data = await api('/archive-queue')
      setQueueData(data)
    } catch (e) {
      console.error('Failed to load archive queue:', e)
    }
    setQueueLoading(false)
  }

  // Auto-refresh archive statuses when any jobs are in-progress
  useEffect(() => {
    const hasActive = Object.values(archiveStatuses).some(s =>
      ['preparing', 'queued', 'transferring'].includes(s.status)
    )
    if (hasActive) {
      archiveRefreshRef.current = setInterval(() => {
        if (videos.length) loadArchiveStatuses(videos)
        if (showQueue) loadQueue()
      }, 10000)
    }
    return () => {
      if (archiveRefreshRef.current) clearInterval(archiveRefreshRef.current)
    }
  }, [archiveStatuses, videos, showQueue])

  useEffect(() => {
    loadVideos()
  }, [loadVideos])

  async function handleSelect(uid) {
    try {
      setStatus('Loading video details...')
      const data = await api(`/videos/${uid}`)
      setSelected(data.result || data)
      setStatus('')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleUpdate(uid, updates) {
    try {
      setStatus('Saving...')
      await api(`/videos/${uid}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      })
      
      const data = await api(`/videos/${uid}`)
      setSelected(data.result || data)
      setStatus('✅ Saved successfully')
      loadVideos()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
      throw e
    }
  }

  async function handleDelete(uid) {
    try {
      setStatus('Deleting...')
      await api(`/videos/${uid}`, { method: 'DELETE' })
      setSelected(null)
      setStatus('✅ Video deleted')
      loadVideos()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
      throw e
    }
  }

  function handleSearch(term) {
    setPaging(p => ({ ...p, search: term, page: 1 }))
  }

  function handleTagFilter(tag) {
    setActiveTag(tag)
  }

  function handlePageChange(newPage) {
    setPaging(p => ({ ...p, page: newPage }))
  }

  function handlePerPageChange(perPage) {
    setPaging(p => ({ ...p, perPage, page: 1 }))
  }

  async function handleUploadComplete(uid) {
    setStatus('✅ Upload complete!')
    loadVideos()
    if (uid) {
      setTimeout(() => handleSelect(uid), 1000)
    }
  }

  // Bulk selection helpers
  function handleToggle(uid) {
    setChecked(prev => {
      const next = new Set(prev)
      next.has(uid) ? next.delete(uid) : next.add(uid)
      return next
    })
  }

  function handleToggleAll(displayVideos) {
    if (checked.size === displayVideos.length && displayVideos.every(v => checked.has(v.uid))) {
      setChecked(new Set())
    } else {
      setChecked(new Set(displayVideos.map(v => v.uid)))
    }
  }

  function clearSelection() {
    setChecked(new Set())
  }

  function getCheckedItems() {
    return videos
      .filter(v => checked.has(v.uid))
      .map(v => ({ id: v.uid, title: v.meta?.name || v.uid }))
  }

  // Bulk: Request Cloudflare to create MP4 downloads for selected videos
  async function handleBulkRequestDownloads() {
    const uids = [...checked]
    setBulkBusy(true)
    setStatus(`Requesting downloads for ${uids.length} videos...`)
    let success = 0
    let errors = 0

    const newTracking = {}
    for (const uid of uids) {
      try {
        await api(`/videos/${uid}/downloads`, { method: 'POST' })
        const video = videos.find(v => v.uid === uid)
        newTracking[uid] = {
          title: video?.meta?.name || uid,
          status: 'inprogress',
          pctComplete: null,
          requestedAt: new Date().toISOString(),
        }
        success++
        setStatus(`Requesting downloads... ${success}/${uids.length}`)
      } catch (e) {
        errors++
        console.error(`Failed to request download for ${uid}:`, e)
      }
    }

    setDownloadTracking(prev => ({ ...prev, ...newTracking }))
    setStatus(`✅ ${success} download${success !== 1 ? 's' : ''} requested${errors ? `, ${errors} failed` : ''}`)
    setBulkBusy(false)
    // Auto-open queue to show progress
    if (!showQueue) { setShowQueue(true); loadQueue() }
  }

  // Poll download statuses for tracked items
  async function pollDownloadStatuses() {
    const activeUids = Object.entries(downloadTracking)
      .filter(([, v]) => v.status === 'inprogress')
      .map(([uid]) => uid)
    if (!activeUids.length) return

    try {
      const data = await api('/download-status/batch', {
        method: 'POST',
        body: JSON.stringify({ uids: activeUids })
      })
      if (data.results) {
        setDownloadTracking(prev => {
          const updated = { ...prev }
          for (const [uid, info] of Object.entries(data.results)) {
            if (updated[uid]) {
              updated[uid] = { ...updated[uid], status: info.status, pctComplete: info.pctComplete }
            }
          }
          return updated
        })
      }
    } catch (e) {
      console.error('Failed to poll download statuses:', e)
    }
  }

  // Auto-poll downloads every 8 seconds while any are in-progress
  useEffect(() => {
    const hasActive = Object.values(downloadTracking).some(d => d.status === 'inprogress')
    if (hasActive) {
      downloadRefreshRef.current = setInterval(pollDownloadStatuses, 8000)
    }
    return () => {
      if (downloadRefreshRef.current) clearInterval(downloadRefreshRef.current)
    }
  }, [downloadTracking])

  // Bulk: Archive selected to R2 (via queue)
  async function handleBulkArchive() {
    setShowArchiveConfirm(true)
  }

  async function executeBulkArchive(deleteAfter) {
    setShowArchiveConfirm(false)
    const uids = [...checked]

    setBulkBusy(true)
    setStatus(`Archiving ${uids.length} videos...`)
    let success = 0
    let errors = 0

    for (const uid of uids) {
      try {
        const video = videos.find(v => v.uid === uid)
        await api(`/archive/${uid}`, {
          method: 'POST',
          body: JSON.stringify({
            title: video?.meta?.name || uid,
            deleteAfter
          })
        })
        success++
        setStatus(`Archiving... ${success}/${uids.length}`)
      } catch (e) {
        errors++
        console.error(`Failed to archive ${uid}:`, e)
      }
    }

    setStatus(`✅ ${success} archive job${success !== 1 ? 's' : ''} queued${errors ? `, ${errors} failed` : ''}${deleteAfter ? ' (will delete from Stream when complete)' : ''}`)
    setBulkBusy(false)
    clearSelection()
    if (deleteAfter) setTimeout(loadVideos, 5000)
  }

  // Bulk: Delete from Stream
  async function handleBulkDelete() {
    const uids = [...checked]
    if (!confirm(`Delete ${uids.length} videos from Stream?\n\nThis cannot be undone!`)) return

    setBulkBusy(true)
    setStatus(`Deleting ${uids.length} videos...`)
    let success = 0
    let errors = 0

    for (const uid of uids) {
      try {
        await api(`/videos/${uid}`, { method: 'DELETE' })
        success++
        setStatus(`Deleting... ${success}/${uids.length}`)
      } catch {
        errors++
      }
    }

    setStatus(`✅ ${success} deleted${errors ? `, ${errors} failed` : ''}`)
    setBulkBusy(false)
    clearSelection()
    setSelected(null)
    loadVideos()
  }

  // Bulk: Verify archive status for selected videos
  async function handleBulkVerify() {
    const uids = [...checked]
    setBulkBusy(true)
    setStatus(`Verifying archives for ${uids.length} videos...`)
    try {
      const data = await api('/archive-status/batch', {
        method: 'POST',
        body: JSON.stringify({ uids })
      })
      if (data.results) {
        setArchiveStatuses(prev => ({ ...prev, ...data.results }))
        const done = Object.values(data.results).filter(r => r.status === 'done').length
        const failed = Object.values(data.results).filter(r => r.status === 'error' || r.status === 'not_found').length
        const none = Object.values(data.results).filter(r => r.status === 'none').length
        setStatus(`✅ Verified: ${done} archived, ${failed} failed, ${none} not archived`)
      }
    } catch (e) {
      setStatus(`Error verifying: ${e.message}`)
    }
    setBulkBusy(false)
  }

  // Bulk: Retry archive for selected videos (reset marker + re-queue)
  async function handleBulkRetryArchive() {
    const uids = [...checked]
    setBulkBusy(true)
    setStatus(`Retrying archive for ${uids.length} videos...`)
    let success = 0
    let errors = 0

    for (const uid of uids) {
      try {
        // Reset marker first to clear stuck state
        await api(`/archive-reset/${uid}`, { method: 'POST' })
        // Re-queue the archive
        const video = videos.find(v => v.uid === uid)
        await api(`/archive/${uid}`, {
          method: 'POST',
          body: JSON.stringify({ title: video?.meta?.name || uid })
        })
        success++
        setStatus(`Retrying... ${success}/${uids.length}`)
      } catch (e) {
        errors++
        console.error(`Failed to retry archive ${uid}:`, e)
      }
    }

    setStatus(`✅ ${success} archive retry${success !== 1 ? 's' : ''} queued${errors ? `, ${errors} failed` : ''}`)
    setBulkBusy(false)
    // Refresh archive statuses after a delay
    setTimeout(() => loadArchiveStatuses(videos), 3000)
  }

  // Queue: Remove a job (delete marker, reset to "not archived")
  async function handleQueueRemove(uid) {
    const job = queueData?.jobs?.find(j => j.uid === uid)
    const isActive = job && ['preparing', 'queued', 'transferring'].includes(job.state)
    if (isActive && !confirm(`"${job.title || uid}" is ${job.state}. Remove from queue and cancel archive?`)) return

    try {
      await api(`/archive-reset/${uid}`, { method: 'POST' })
      setArchiveStatuses(prev => {
        const updated = { ...prev }
        delete updated[uid]
        return updated
      })
      setStatus(`✅ Removed "${job?.title || uid}" from queue`)
      loadQueue()
      loadArchiveStatuses(videos)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  // Queue: Retry a failed/stuck job (reset marker + re-queue)
  async function handleQueueRetry(uid) {
    try {
      const job = queueData?.jobs?.find(j => j.uid === uid)
      await api(`/archive-reset/${uid}`, { method: 'POST' })
      await api(`/archive/${uid}`, {
        method: 'POST',
        body: JSON.stringify({ title: job?.title || uid })
      })
      setStatus(`✅ Re-queued "${job?.title || uid}"`)
      loadQueue()
      loadArchiveStatuses(videos)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  // Queue: Reset all stuck jobs
  async function handleResetAllStuck() {
    const stuck = queueData?.jobs?.filter(j => j.isStuck) || []
    if (!stuck.length) return
    if (!confirm(`Reset ${stuck.length} stuck job(s)? This clears their markers so they can be retried.`)) return

    setStatus(`Resetting ${stuck.length} stuck jobs...`)
    for (const job of stuck) {
      try {
        await api(`/archive-reset/${job.uid}`, { method: 'POST' })
      } catch {}
    }
    setStatus(`✅ Reset ${stuck.length} stuck jobs`)
    loadQueue()
    loadArchiveStatuses(videos)
  }

  // Bulk: Generate AI captions
  async function handleBulkCaptions() {
    const lang = prompt('Language code for captions (e.g. en, es, fr, de):', 'en')
    if (!lang) return

    const uids = [...checked]
    setBulkBusy(true)
    setStatus(`Generating ${lang} captions for ${uids.length} videos...`)
    let success = 0
    let errors = 0

    for (const uid of uids) {
      try {
        // Delete existing caption for this language first (ignore errors if none exists)
        try { await api(`/videos/${uid}/captions/${lang}`, { method: 'DELETE' }) } catch {}
        await api(`/videos/${uid}/captions/${lang}/generate`, { method: 'POST' })
        success++
        setStatus(`Generating captions... ${success}/${uids.length}`)
      } catch (e) {
        errors++
        console.error(`Failed captions for ${uid}:`, e)
      }
    }

    setStatus(`✅ ${success} caption job${success !== 1 ? 's' : ''} started${errors ? `, ${errors} failed` : ''}`)
    setBulkBusy(false)
  }

  // Apply tag + archive filters client-side for table mode
  let displayVideos = tagFilterUIDs !== null
    ? videos.filter(v => tagFilterUIDs.includes(v.uid))
    : videos

  if (archiveFilter) {
    displayVideos = displayVideos.filter(v => {
      const s = archiveStatuses[v.uid]?.status || 'none'
      if (archiveFilter === 'in_progress') return s === 'preparing' || s === 'queued' || s === 'transferring'
      if (archiveFilter === 'error') return s === 'error' || s === 'not_found'
      return s === archiveFilter
    })
  }

  return (
    <div className="stream-manager">
      <div className="stream-manager-header">
        <h1>Stream Manager</h1>
        <div className="sm-header-right">
          <div className="sm-view-toggle">
            <button
              className={`sm-view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Card view"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              className={`sm-view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table view"
            >
              <LayoutList size={16} />
            </button>
          </div>
          <button
            className={`sm-queue-toggle ${showQueue ? 'active' : ''}`}
            onClick={() => { setShowQueue(q => !q); if (!showQueue) loadQueue() }}
            title="Archive Queue"
          >
            <Activity size={14} /> Queue
            {((queueData?.summary?.active || 0) + Object.values(downloadTracking).filter(d => d.status === 'inprogress').length) > 0 && (
              <span className="queue-active-count">
                {(queueData?.summary?.active || 0) + Object.values(downloadTracking).filter(d => d.status === 'inprogress').length}
              </span>
            )}
          </button>
          <span className="status-text">{status}</span>
        </div>
      </div>

      {showQueue && (
        <div className="archive-queue-panel">
          <div className="aq-header">
            <h3><Activity size={14} /> Archive Queue</h3>
            <div className="aq-header-actions">
              {queueData?.summary?.stuck > 0 && (
                <button className="aq-btn-warn" onClick={handleResetAllStuck}>
                  <AlertTriangle size={12} /> Reset {queueData.summary.stuck} Stuck
                </button>
              )}
              <button className="aq-btn" onClick={loadQueue} disabled={queueLoading}>
                <RefreshCw size={12} className={queueLoading ? 'spinning' : ''} /> Refresh
              </button>
            </div>
          </div>

          {queueData?.summary && (
            <div className="aq-summary">
              <span className="aq-stat"><Clock size={11} /> {queueData.summary.active} active</span>
              {queueData.summary.stuck > 0 && <span className="aq-stat aq-stuck"><AlertTriangle size={11} /> {queueData.summary.stuck} stuck</span>}
              <span className="aq-stat aq-done"><CheckCircle size={11} /> {queueData.summary.done} done</span>
              {queueData.summary.error > 0 && <span className="aq-stat aq-error"><XCircle size={11} /> {queueData.summary.error} errors</span>}
            </div>
          )}

          {queueLoading && !queueData ? (
            <div className="aq-loading">Loading queue...</div>
          ) : queueData?.jobs?.length === 0 ? (
            <div className="aq-empty">No archive jobs found</div>
          ) : (
            <div className="aq-jobs">
              {queueData?.jobs?.filter(j => j.state !== 'done' || j.isStuck).slice(0, 50).map(job => (
                <div key={job.uid} className={`aq-job ${job.isStuck ? 'stuck' : ''} ${job.state}`}>
                  <div className="aq-job-info">
                    {job.queuePosition && (
                      <span className="aq-job-pos">#{job.queuePosition}</span>
                    )}
                    <span className="aq-job-title">{job.title || job.filename?.replace(/\.mp4$/i, '') || job.uid}</span>
                    <span className={`aq-job-state aq-state-${job.state}`}>
                      {job.state === 'transferring' && job.progress ? `${job.progress}%`
                        : job.state === 'queued' ? `queued`
                        : job.state}
                    </span>
                    {job.isStuck && <span className="aq-job-stuck-badge"><AlertTriangle size={10} /> stuck {job.minutesSinceUpdate}m</span>}
                    {job.message && <span className="aq-job-msg">{job.message}</span>}
                  </div>
                  <div className="aq-job-actions">
                    {(job.state === 'error' || job.isStuck) && (
                      <button className="aq-btn-sm" onClick={() => handleQueueRetry(job.uid)} title="Reset and re-queue">
                        <RefreshCw size={11} /> Retry
                      </button>
                    )}
                    <button className="aq-btn-sm aq-btn-remove" onClick={() => handleQueueRemove(job.uid)} title="Remove from queue">
                      <XCircle size={11} /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Download Requests Section */}
          {Object.keys(downloadTracking).length > 0 && (
            <>
              <div className="aq-section-divider">
                <h4><Download size={12} /> Download Requests</h4>
                <button className="aq-btn-sm" onClick={() => {
                  setDownloadTracking(prev => {
                    const kept = {}
                    for (const [uid, d] of Object.entries(prev)) {
                      if (d.status === 'inprogress') kept[uid] = d
                    }
                    return kept
                  })
                }} title="Clear completed">Clear Done</button>
              </div>
              <div className="aq-jobs">
                {Object.entries(downloadTracking).map(([uid, dl]) => (
                  <div key={uid} className={`aq-job ${dl.status}`}>
                    <div className="aq-job-info">
                      <span className="aq-job-title">{dl.title}</span>
                      <span className={`aq-job-state aq-state-dl-${dl.status}`}>
                        {dl.status === 'inprogress' && dl.pctComplete
                          ? `${dl.pctComplete}%`
                          : dl.status === 'ready' ? 'Ready' : dl.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className={`stream-manager-layout ${viewMode === 'table' ? 'sm-layout-table' : 'sm-layout-cards'}`}>
        {viewMode === 'cards' ? (
          <>
            {/* Cards mode: sidebar card list + detail right */}
            <div className="stream-manager-sidebar">
              <VideoList
                videos={videos}
                selected={selected}
                loading={loading}
                paging={paging}
                onSelect={handleSelect}
                onSearch={handleSearch}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                allTags={allTags}
                activeTag={activeTag}
                onTagFilter={handleTagFilter}
                tagFilterUIDs={tagFilterUIDs}
                checked={checked}
                onToggle={handleToggle}
                onToggleAll={handleToggleAll}
                onClearSelection={clearSelection}
                onBulkTag={() => setShowBulkTag(true)}
                onBulkRequestDownloads={handleBulkRequestDownloads}
                onBulkArchive={handleBulkArchive}
                onBulkDelete={handleBulkDelete}
                onBulkCaptions={handleBulkCaptions}
                onBulkVerify={handleBulkVerify}
                onBulkRetryArchive={handleBulkRetryArchive}
                bulkBusy={bulkBusy}
                vodLinks={vodLinks}
                archiveStatuses={archiveStatuses}
              />
            </div>

            <div className="stream-manager-main">
              {selected ? (
                <VideoDetail
                  video={selected}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                  setStatus={setStatus}
                />
              ) : (
                <div className="no-selection">
                  <p>Select a video from the list to view details</p>
                </div>
              )}

              <VideoUpload onComplete={handleUploadComplete} setStatus={setStatus} />
            </div>
          </>
        ) : (
          <>
            {/* Table mode: detail on top (split), filters + table on bottom */}
            <div className="sm-detail-panel">
              {selected ? (
                <VideoDetail
                  video={selected}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                  setStatus={setStatus}
                  layout="split"
                  uploadSlot={<VideoUpload onComplete={handleUploadComplete} setStatus={setStatus} />}
                />
              ) : (
                <div className="no-selection">
                  <p>Select a video from the table below to view details</p>
                  <VideoUpload onComplete={handleUploadComplete} setStatus={setStatus} />
                </div>
              )}
            </div>

            {/* Filters bar */}
            <div className="sm-filters-bar">
              <form className="sm-filter-search" onSubmit={e => { e.preventDefault(); handleSearch(e.target.querySelector('input').value) }}>
                <input
                  type="text"
                  placeholder="Search videos..."
                  defaultValue={paging.search}
                />
                <button type="submit">Search</button>
              </form>

              <div className="sm-filter-tag">
                <select value={activeTag} onChange={e => handleTagFilter(e.target.value)}>
                  <option value="">All Tags</option>
                  {(allTags || []).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div className="sm-filter-archive">
                <select value={archiveFilter} onChange={e => setArchiveFilter(e.target.value)}>
                  <option value="">All Archives</option>
                  <option value="done">Archived</option>
                  <option value="error">Failed</option>
                  <option value="in_progress">In Progress</option>
                  <option value="none">Not Archived</option>
                </select>
              </div>

              <div className="sm-filter-pagination">
                <select value={paging.perPage} onChange={e => handlePerPageChange(Number(e.target.value))}>
                  <option value={25}>25/pg</option>
                  <option value={50}>50/pg</option>
                  <option value={100}>100/pg</option>
                </select>
                <button onClick={() => handlePageChange(paging.page - 1)} disabled={paging.page <= 1}>‹</button>
                <span>Pg {paging.page}</span>
                <button onClick={() => handlePageChange(paging.page + 1)}>›</button>
              </div>

              {checked.size > 0 && (
                <div className="sm-filter-bulk">
                  <span>{checked.size} sel</span>
                  <button onClick={() => setShowBulkTag(true)} disabled={bulkBusy}>Tag</button>
                  <button onClick={handleBulkCaptions} disabled={bulkBusy}>Captions</button>
                  <button onClick={handleBulkRequestDownloads} disabled={bulkBusy}>Downloads</button>
                  <button onClick={handleBulkArchive} disabled={bulkBusy}>Archive</button>
                  <button onClick={handleBulkVerify} disabled={bulkBusy}>Verify</button>
                  <button onClick={handleBulkRetryArchive} disabled={bulkBusy}>Retry Archive</button>
                  <button onClick={handleBulkDelete} disabled={bulkBusy} className="danger">Delete</button>
                  <button onClick={clearSelection}>✕</button>
                </div>
              )}

            </div>

            <StreamTable
              videos={displayVideos}
              loading={loading}
              selected={selected}
              onSelect={handleSelect}
              checked={checked}
              onToggle={handleToggle}
              onToggleAll={handleToggleAll}
              vodLinks={vodLinks}
              archiveStatuses={archiveStatuses}
              downloadStatuses={downloadTracking}
            />
          </>
        )}
      </div>

      {showBulkTag && (
        <BulkTagModal
          items={getCheckedItems()}
          idKey="stream_uid"
          onClose={() => setShowBulkTag(false)}
          onComplete={() => {
            setShowBulkTag(false)
            setStatus('✅ Bulk tags applied')
            clearSelection()
            api('/tags').then(d => setAllTags(d.tags || [])).catch(() => {})
          }}
        />
      )}

      {showArchiveConfirm && (
        <ConfirmArchiveModal
          count={checked.size}
          onConfirm={executeBulkArchive}
          onCancel={() => setShowArchiveConfirm(false)}
        />
      )}
    </div>
  )
}
